import { useCallback, useEffect, useState } from 'react';
import { DeviceCreate } from '../../../api/models';
import { deviceService, overrideCustomFieldsOf } from '../../../services/deviceService';
import { CustomField } from '../../../types/configTemplate';
import { categorizeDeviceFieldsFromSchema, FieldGroupData, getEditableFields, buildOverrides } from '../../../utils/fieldGrouping';
import { DeviceDetailCore } from '../useDeviceDetail';

/**
 * The Settings panel: the effective configuration grouped by the fixed
 * key list (schema v3), with the override editor. Every edited key goes to
 * the device's config_override; there are no device-row config columns
 * any more.
 */
export const useDevicesDetailsSettings = (core: DeviceDetailCore) => {
  const { device, isOpen, deviceDetail, setDeviceDetail, setError, fieldDefinitions, onDeviceUpdated } = core;

  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['mqtt', 'syslog', 'general', 'additional']);
  const [categorizedFields, setCategorizedFields] = useState<FieldGroupData[]>([]);
  const [settingsForm, setSettingsForm] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!isOpen) return;
    setIsEditingSettings(false);
  }, [isOpen, device?.idDevice]);

  // The fixed keys, valued from the effective configuration.
  useEffect(() => {
    setCategorizedFields(deviceDetail ? categorizeDeviceFieldsFromSchema(deviceDetail, fieldDefinitions) : []);
  }, [deviceDetail, fieldDefinitions]);

  // The device's own custom fields, as stored in its override.
  useEffect(() => {
    setCustomFields(overrideCustomFieldsOf(deviceDetail).map((c) => ({ name: c.name, value: String(c.value ?? '') })));
  }, [deviceDetail]);

  const initSettingsForm = useCallback(() => {
    if (categorizedFields.length > 0) {
      setSettingsForm(getEditableFields(categorizedFields));
    }
  }, [categorizedFields]);

  const toggleGroupExpanded = useCallback((groupCode: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupCode) ? prev.filter((code) => code !== groupCode) : [...prev, groupCode],
    );
  }, []);

  const handleSettingsChange = (field: string, value: string | number) => {
    setSettingsForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleStartSettingsEdit = () => {
    initSettingsForm();
    setIsEditingSettings(true);
  };

  const handleCancelSettingsEdit = () => {
    initSettingsForm();
    setIsEditingSettings(false);
  };

  const handleSaveSettings = async () => {
    if (!device || !deviceDetail) return;
    setSavingSettings(true);
    setError(null);
    try {
      // What changed goes to the device's overrides.
      const originalValues = getEditableFields(categorizedFields);
      const overrides: Record<string, unknown> = buildOverrides(originalValues, settingsForm);
      if (customFields.length > 0) {
        overrides.custom_fields = customFields;
      }
      if (Object.keys(overrides).length === 0) {
        setIsEditingSettings(false);
        return;
      }
      const updatedDetail = await deviceService.overrideConfig(device.idDevice, overrides);
      setDeviceDetail(updatedDetail);
      setIsEditingSettings(false);
      const cfg = (updatedDetail.effective_config || {}) as Record<string, any>;
      onDeviceUpdated?.({
        ...device,
        mqttHost: cfg.mqtt_host,
        mqttPort: cfg.mqtt_port,
        syslogHost: cfg.syslog_host,
        syslogPort: cfg.syslog_port,
      } as DeviceCreate);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
      console.error('Failed to save settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  return {
    isEditingSettings, savingSettings,
    customFields, setCustomFields,
    expandedGroups, toggleGroupExpanded,
    categorizedFields, settingsForm, handleSettingsChange,
    handleStartSettingsEdit, handleCancelSettingsEdit, handleSaveSettings,
  };
};
